import { useState } from 'react';
import { formatCentsPlain, parseCents } from '@amai/contracts';

/**
 * Campo de dinheiro.
 *
 * O operador digita como fala ("1.234,56" ou "1234,56"); o valor sai em
 * CENTAVOS. A formatação acontece ao sair do campo, não a cada tecla — cursor
 * pulando enquanto se digita é o que faz gente desistir de usar o sistema.
 */
export function MoneyInput({
  value,
  onChange,
  id,
  placeholder = '0,00',
  disabled,
  invalid,
  ariaLabel,
}: {
  value: number | null;
  onChange: (cents: number | null) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /**
   * Nome do campo quando NÃO há um `<label for>` apontando para ele — é o
   * caso da linha de tabela, onde o rótulo mora no cabeçalho da coluna e não
   * pode ser amarrado a cada célula.
   *
   * Existe porque o componente não espalha props soltas: um `aria-label`
   * passado por fora era descartado em silêncio, e o campo ficava sem nome
   * nenhum para quem usa leitor de tela.
   */
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const display = draft ?? (value === null ? '' : formatCentsPlain(value));

  return (
    <div style={{ position: 'relative' }}>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 12.5,
          color: 'var(--ink-faint)',
          pointerEvents: 'none',
        }}
      >
        R$
      </span>
      <input
        id={id}
        className="input input--mono"
        style={{ paddingLeft: 34, textAlign: 'right' }}
        inputMode="decimal"
        aria-label={ariaLabel}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid}
        value={display}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (draft === null) return;
          onChange(draft.trim() === '' ? null : parseCents(draft));
          setDraft(null);
        }}
      />
    </div>
  );
}

/** Mesmo comportamento, para quantidade em milésimos. */
export function QtyInput({
  value,
  onChange,
  unit,
  id,
  disabled,
  invalid,
  ariaLabel,
}: {
  value: number | null;
  onChange: (milli: number | null) => void;
  unit?: string;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Ver `MoneyInput`: nome do campo na linha de tabela, sem `<label for>`. */
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const display =
    draft ??
    (value === null
      ? ''
      : (value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 3 }));

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        className="input input--mono"
        style={{ textAlign: 'right', paddingRight: unit ? 44 : 12 }}
        inputMode="decimal"
        aria-label={ariaLabel}
        placeholder="0"
        disabled={disabled}
        aria-invalid={invalid}
        value={display}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (draft === null) return;
          const cleaned = draft.trim();
          if (cleaned === '') {
            onChange(null);
          } else {
            const normalized = cleaned.includes(',')
              ? cleaned.replace(/\./g, '').replace(',', '.')
              : cleaned;
            const parsed = Number(normalized);
            onChange(Number.isNaN(parsed) ? null : Math.round(parsed * 1000));
          }
          setDraft(null);
        }}
      />
      {unit && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 11.5,
            color: 'var(--ink-faint)',
            pointerEvents: 'none',
          }}
        >
          {unit}
        </span>
      )}
    </div>
  );
}
